/**
 * HospitalBackordersBoard
 * -----------------------
 * Hospital-facing command-center view, mirrors the admin board but with
 * hospital-appropriate scoping:
 *
 *   - Uses GET /api/hospital/my-backorders (only the hospital's own requests)
 *   - Read-only summary card on each request, no editing of items / ship-to
 *   - Status pills + sourcing counters so they can see "Sterile Mate emailed
 *     12 suppliers, 3 quotes received" without needing to call us
 *   - Delete button on requests still in `new` status
 *   - "+ New Backorder Request" button pops the same form as the homepage,
 *     pre-filling the user's hospital name from their profile
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Plus, RefreshCw, X, ChevronRight, Trash2,
  AlertCircle, CheckCircle2, Clock, Package, Truck, MapPin,
  Loader2, FileText, ListChecks, Pause, Mail, Send, Phone,
  Edit3, BellRing,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new:               { label: "New",        cls: "bg-blue-50 text-blue-700 border-blue-200" },
  sourcing:          { label: "Sourcing",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent_to_suppliers: { label: "RFQ Sent",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  quotes_received:   { label: "Quotes In",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  awarded:           { label: "Awarded",    cls: "bg-violet-50 text-violet-700 border-violet-200" },
  fulfilled:         { label: "Fulfilled",  cls: "bg-slate-100 text-slate-700 border-slate-300" },
  on_hold:           { label: "On Hold",    cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  cancelled:         { label: "Cancelled",  cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${meta.cls}`}>{meta.label}</span>;
}

function fmtAge(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}


export default function HospitalBackordersBoard({ user, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hospital/my-backorders`, { headers });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load your backorders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const base = { all: items.length, new: 0, sourcing: 0, quotes_received: 0, awarded: 0, fulfilled: 0 };
    items.forEach((it) => {
      const key = (it.status === "sent_to_suppliers") ? "sourcing" : (it.status || "new");
      if (base[key] !== undefined) base[key] += 1;
    });
    return base;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== "all") {
        const key = (it.status === "sent_to_suppliers") ? "sourcing" : (it.status || "new");
        if (key !== statusFilter) return false;
      }
      if (!q) return true;
      const hay = `${it.id || ""} ${it.item_name || ""} ${it.shipping_address || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, statusFilter]);

  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  const deleteRequest = async (id) => {
    const yes = window.confirm(
      `Cancel and delete backorder ${id}?\n\nYou can only delete requests that haven't been sent out to suppliers yet. This cannot be undone.`,
    );
    if (!yes) return;
    try {
      await axios.delete(`${API}/api/hospital/my-backorders/${id}`, { headers });
      toast.success("Request deleted");
      if (selectedId === id) setSelectedId(null);
      setItems((prev) => prev.filter((it) => it.id !== id));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="hospital-backorders-board">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Backorder Requests</h1>
          <p className="text-sm text-slate-500">
            Track every item your team has requested through Sterile Mate's sourcing desk.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="hospital-bo-new-btn">
          <Plus className="w-4 h-4" /> New Backorder Request
        </Button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { key: "all",             label: "All requests", icon: Package,      color: "bg-slate-100 text-slate-700" },
          { key: "new",             label: "Pending",      icon: AlertCircle,  color: "bg-blue-50 text-blue-700" },
          { key: "sourcing",        label: "Sourcing",     icon: Send,         color: "bg-amber-50 text-amber-700" },
          { key: "quotes_received", label: "Quotes In",    icon: ListChecks,   color: "bg-emerald-50 text-emerald-700" },
          { key: "awarded",         label: "Awarded",      icon: CheckCircle2, color: "bg-violet-50 text-violet-700" },
          { key: "fulfilled",       label: "Fulfilled",    icon: CheckCircle2, color: "bg-slate-50 text-slate-700" },
        ].map((k) => {
          const active = statusFilter === k.key;
          return (
            <button
              key={k.key}
              onClick={() => setStatusFilter(k.key)}
              className={`text-left rounded-xl border p-3 transition-all bg-white ${active ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
              data-testid={`hospital-bo-kpi-${k.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${k.color}`}>
                  <k.icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
              </div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{stats[k.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by item or ship-to…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left">Request</th>
                <th className="px-4 py-2 text-left">Items</th>
                <th className="px-4 py-2 text-left">Ship-to</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Sourcing</th>
                <th className="px-4 py-2 text-left">Submitted</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                  <p className="mb-3">No backorder requests yet.</p>
                  <Button onClick={() => setShowNew(true)} size="sm" className="bg-amber-600 hover:bg-amber-700 gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Submit your first request
                  </Button>
                </td></tr>
              )}
              {!loading && filtered.map((it) => {
                const its = it.items || [];
                const firstItem = its[0]?.name || it.item_name || "—";
                const extra = its.length > 1 ? `+${its.length - 1} more` : "";
                const canDelete = (it.status || "new") === "new";
                return (
                  <tr key={it.id} onClick={() => setSelectedId(it.id)} className="hover:bg-amber-50/40 cursor-pointer transition-colors" data-testid={`hospital-bo-row-${it.id}`}>
                    <td className="px-4 py-2.5 align-top">
                      <span className="font-mono text-xs font-bold text-slate-900">{it.id}</span>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-sm font-medium text-slate-900 line-clamp-1">{firstItem}</div>
                      {extra && <div className="text-[11px] text-slate-500">{extra}</div>}
                    </td>
                    <td className="px-4 py-2.5 align-top text-sm text-slate-700">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        {it.shipping_address || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top"><StatusPill status={it.status} /></td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-xs text-slate-700">
                        <span className="font-bold">{it.recipients_count || 0}</span> emailed · <span className="font-bold text-emerald-700">{it.quotes_count || 0}</span> quoted
                      </div>
                      {it.recipients_count > 0 && (
                        <div className="h-1 rounded bg-slate-100 mt-1 overflow-hidden">
                          <div className="h-1 bg-emerald-500"
                            style={{ width: `${Math.min(100, Math.round(((it.quotes_count || 0) / Math.max(1, it.recipients_count)) * 100))}%` }} />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-500">
                      <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtAge(it.created_at)}</div>
                    </td>
                    <td className="px-4 py-2.5 align-top text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedId(it.id); }} className="text-amber-700 hover:text-amber-800 inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-1 rounded hover:bg-amber-50">
                          Open <ChevronRight className="w-3 h-3" />
                        </button>
                        {canDelete && (
                          <button onClick={(e) => { e.stopPropagation(); deleteRequest(it.id); }} title="Cancel & delete" className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition-colors" data-testid={`hospital-bo-delete-${it.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <HospitalDetailDrawer
          backorder={selected}
          onClose={() => setSelectedId(null)}
          onDelete={selected.status === "new" || !selected.status ? () => deleteRequest(selected.id) : null}
          onChanged={load}
          token={token}
        />
      )}

      {showNew && (
        <NewHospitalBackorderModal
          open={showNew}
          onClose={() => setShowNew(false)}
          onSuccess={load}
          user={user}
          token={token}
        />
      )}
    </div>
  );
}


// ============================================================================
function HospitalDetailDrawer({ backorder, onClose, onDelete, onChanged, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [draft, setDraft] = useState(backorder);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPingBox, setShowPingBox] = useState(false);
  const [pingMsg, setPingMsg] = useState("");
  const [pinging, setPinging] = useState(false);

  useEffect(() => { setDraft(backorder); }, [backorder]);

  const canEdit = (draft.status || "new") === "new";

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/api/hospital/my-backorders/${backorder.id}`, {
        notes: draft.notes,
        shipping_address: draft.shipping_address,
        contact_phone: draft.contact_phone,
        use_hospital_courier: !!draft.use_hospital_courier,
        hospital_courier_account: draft.hospital_courier_account || "",
      }, { headers });
      toast.success("Backorder updated");
      setEditing(false);
      setDraft(r.data.backorder);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    } finally { setSaving(false); }
  };

  const sendPing = async () => {
    setPinging(true);
    try {
      await axios.post(`${API}/api/hospital/my-backorders/${backorder.id}/request-update`,
        { message: pingMsg || "Hospital requested a status update." },
        { headers });
      toast.success("Sterile Mate has been notified");
      setShowPingBox(false);
      setPingMsg("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setPinging(false); }
  };

  const emailSterileMate = () => {
    const subject = `Question re: backorder ${backorder.id}`;
    const body = (
      `Hi Sterile Mate team,\n\nQuestion about my backorder ${backorder.id}.\n\n` +
      `Hospital: ${backorder.hospital_name || ""}\n` +
      `Item: ${backorder.item_name || ""}\n` +
      `Current status: ${backorder.status || "new"}\n\n` +
      `My question:\n\n\n` +
      `Thanks,\n${backorder.contact_name || ""}`
    );
    window.location.href = `mailto:orders@sterilemate.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose}>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold text-slate-900">{draft.id}</span>
              <StatusPill status={draft.status} />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Backorder Request</h2>
            <p className="text-xs text-slate-500">{fmtAge(draft.created_at)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Action toolbar */}
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-white">
          {canEdit && (
            <Button
              size="sm" variant="outline"
              onClick={() => editing ? saveEdits() : setEditing(true)}
              disabled={saving}
              className="gap-1.5"
              data-testid="hospital-bo-edit"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
              {editing ? "Save" : "Edit"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowPingBox((v) => !v)} className="gap-1.5" data-testid="hospital-bo-ping">
            <BellRing className="w-3.5 h-3.5" /> Request Update
          </Button>
          <Button size="sm" variant="outline" onClick={emailSterileMate} className="gap-1.5" data-testid="hospital-bo-email">
            <Mail className="w-3.5 h-3.5" /> Email Sterile Mate
          </Button>
        </div>

        {showPingBox && (
          <div className="px-5 py-3 border-b border-slate-200 bg-amber-50/60 space-y-2">
            <p className="text-xs font-semibold text-slate-700">What would you like an update on?</p>
            <Textarea
              rows={2}
              placeholder="e.g., Need to know if any suppliers have responded…"
              value={pingMsg}
              onChange={(e) => setPingMsg(e.target.value)}
              data-testid="hospital-bo-ping-msg"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowPingBox(false)}>Cancel</Button>
              <Button size="sm" onClick={sendPing} disabled={pinging} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="hospital-bo-ping-submit">
                {pinging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Request
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Items */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> Items requested
            </h3>
            <div className="space-y-1.5">
              {(backorder.items || []).length === 0 && backorder.item_name && (
                <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm">
                  <strong>{backorder.item_name}</strong> · Qty: {backorder.quantity || 1}
                </div>
              )}
              {(backorder.items || []).map((it, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-900">{idx + 1}. {it.name}</span>
                  <span className="text-slate-600"> · Qty: {it.quantity}</span>
                  {it.notes && <div className="text-xs text-slate-500 mt-0.5">{it.notes}</div>}
                </div>
              ))}
            </div>
          </section>

          {/* Ship-to + Freight (editable) */}
          <section className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Ship-to {editing && <span className="text-amber-600 font-bold">(editing)</span>}
              </h3>
              {editing ? (
                <Textarea
                  rows={3}
                  value={draft.shipping_address || ""}
                  onChange={(e) => setDraft({ ...draft, shipping_address: e.target.value })}
                  className="text-sm"
                  data-testid="hospital-bo-edit-shipto"
                />
              ) : (
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{draft.shipping_address || "—"}</p>
              )}
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" /> Freight
              </h3>
              {editing ? (
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={!!draft.use_hospital_courier}
                      onChange={(e) => setDraft({ ...draft, use_hospital_courier: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300"
                      data-testid="hospital-bo-edit-own-freight"
                    />
                    <span className="text-slate-700">Use our 3rd-party courier</span>
                  </label>
                  {draft.use_hospital_courier && (
                    <Input
                      placeholder="Carrier — Acct # — Contact"
                      value={draft.hospital_courier_account || ""}
                      onChange={(e) => setDraft({ ...draft, hospital_courier_account: e.target.value })}
                    />
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-800">
                    {draft.use_hospital_courier ? "Using hospital 3rd-party courier" : "Suppliers will quote freight"}
                  </p>
                  {draft.hospital_courier_account && (
                    <p className="text-xs text-slate-500 mt-1 font-mono">{draft.hospital_courier_account}</p>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Notes (editable) */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Your notes {editing && <span className="text-amber-600 font-bold">(editing)</span>}
            </h3>
            {editing ? (
              <Textarea
                rows={3}
                value={draft.notes || ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Add any context you'd like Sterile Mate to know about this request…"
                className="text-sm"
                data-testid="hospital-bo-edit-notes"
              />
            ) : (draft.notes ? (
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2 whitespace-pre-wrap">{draft.notes}</p>
            ) : <p className="text-sm text-slate-400 italic">No notes added.</p>)}
          </section>

          {/* Status / progress */}
          <section className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <ListChecks className="w-3.5 h-3.5" /> Sourcing progress
            </h3>
            <div className="grid grid-cols-2 gap-3 text-center">
              <div>
                <div className="text-2xl font-extrabold text-slate-900">{backorder.recipients_count || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1">Suppliers emailed</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-emerald-700">{backorder.quotes_count || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1">Quotes received</div>
              </div>
            </div>
            {backorder.quotes_count > 0 && (
              <p className="text-xs text-emerald-700 mt-3 text-center font-medium">
                Quotes are in! Your Sterile Mate rep will email you shortly with the leading option for approval.
              </p>
            )}
            {backorder.quotes_count === 0 && backorder.recipients_count > 0 && (
              <p className="text-xs text-amber-700 mt-3 text-center font-medium">
                Sterile Mate is actively sourcing. Suppliers typically respond within 24h.
              </p>
            )}
            {!backorder.recipients_count && (backorder.status || "new") === "new" && (
              <p className="text-xs text-slate-500 mt-3 text-center">
                Sterile Mate's sourcing desk will review and broadcast your request to qualified suppliers.
              </p>
            )}
          </section>

          {/* Help footer */}
          <section className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 space-y-1">
            <p>Questions about this request? Email us or give us a call.</p>
            <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> <a href="mailto:orders@sterilemate.com" className="text-amber-700">orders@sterilemate.com</a></p>
            <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> <a href="tel:2814563458" className="text-amber-700">(281) 456-3458</a></p>
          </section>
        </div>

        {onDelete && (
          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex justify-end">
            <Button variant="outline" onClick={onDelete} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 hover:border-rose-300 gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Cancel & delete request
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
function NewHospitalBackorderModal({ open, onClose, onSuccess, user, token }) {
  const hospitalName =
    user?.hospital_name || user?.facility_name || user?.organization_name || user?.name || "";
  const contactName = user?.name || user?.full_name || "";
  const contactEmail = user?.email || "";
  const contactPhone = user?.phone || "";

  const [items, setItems] = useState([{ name: "", quantity: 1, notes: "" }]);
  const [ship, setShip] = useState({ city: "", state: "", zip: "" });
  const [useCourier, setUseCourier] = useState(false);
  const [courier, setCourier] = useState("");
  const [notes, setNotes] = useState("");
  const [hospitalOverride, setHospitalOverride] = useState(hospitalName);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const addItem = () => setItems((p) => [...p, { name: "", quantity: 1, notes: "" }]);
  const setIt = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    const valid = items.filter((it) => (it.name || "").trim());
    if (valid.length === 0) { toast.error("Add at least one item"); return; }
    if (!hospitalOverride.trim()) { toast.error("Facility name is required"); return; }
    setSubmitting(true);
    try {
      const shipAddr = [ship.city, ship.state, ship.zip].map((s) => s.trim()).filter(Boolean).join(", ");
      await axios.post(`${API}/api/order-request`, {
        order_type: "backorder",
        hospital_name: hospitalOverride,
        address: shipAddr,
        contact_name: contactName || "Hospital User",
        contact_email: contactEmail || "orders@sterilemate.com",
        contact_phone: contactPhone || "",
        item_name: valid.map((it) => it.name).join(", "),
        quantity: valid.reduce((s, it) => s + (parseInt(it.quantity) || 1), 0),
        notes,
        items: valid.map((it) => ({ name: it.name, quantity: parseInt(it.quantity) || 1, notes: it.notes || "" })),
        shipping_address: shipAddr,
        use_hospital_courier: useCourier,
        hospital_courier_account: useCourier ? courier : "",
        ship_via: useCourier ? "Hospital 3rd-Party Courier" : "Best Way",
        terms: "Net 30",
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Request submitted — Sterile Mate is on it");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">New Backorder Request</h2>
            <p className="text-xs text-slate-500">Tell us what you need — we&apos;ll route it to qualified suppliers in minutes.</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Items</p>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input placeholder={`Item ${i + 1} *`} className="col-span-6 h-9" value={it.name} onChange={(e) => setIt(i, "name", e.target.value)} />
                  <Input type="number" min="1" placeholder="Qty" className="col-span-2 h-9" value={it.quantity} onChange={(e) => setIt(i, "quantity", e.target.value)} />
                  <Input placeholder="MFR / notes" className="col-span-3 h-9" value={it.notes} onChange={(e) => setIt(i, "notes", e.target.value)} />
                  {items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-slate-400 hover:text-rose-600">✕</button>}
                </div>
              ))}
              <button type="button" onClick={addItem} className="text-xs font-semibold text-amber-700 hover:text-amber-800">+ Add another item</button>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Facility</p>
            <Input className="h-9" value={hospitalOverride} onChange={(e) => setHospitalOverride(e.target.value)} placeholder="Hospital / facility name" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Ship-to (city / state / zip)</p>
            <div className="grid grid-cols-12 gap-2">
              <Input placeholder="City" className="col-span-6 h-9" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
              <Input placeholder="ST" maxLength={2} className="col-span-2 h-9 uppercase" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value.toUpperCase().slice(0, 2) })} />
              <Input placeholder="ZIP" className="col-span-4 h-9" value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={useCourier} onChange={(e) => setUseCourier(e.target.checked)} className="accent-amber-600 w-4 h-4" />
              <strong className="text-slate-900">Use our facility&apos;s own freight account</strong>
            </label>
            {useCourier && <Input className="h-9 mt-2" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier & account # (e.g. FedEx 1234-5678-9)" />}
          </div>
          <Textarea rows={3} placeholder="Notes (visible to suppliers)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              Submit Request
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
